import { App } from "vue";
import { Router } from "vue-router";
import CLocalizedLink from "@/components/CLocalizedLink.vue";

// Available locales in the application
const availableLocales = ["en", "ru", "uz", "ar"];

/**
 * Get the current locale from a path
 */
export function getLocaleFromPath(path: string): string {
  const pathSegments = path.split("/");
  const potentialLocale = pathSegments.length > 1 ? pathSegments[1] : "";
  return availableLocales.includes(potentialLocale) ? potentialLocale : "en";
}

/**
 * Get the path without locale prefix
 */
export function getPathWithoutLocale(path: string): string {
  const locale = getLocaleFromPath(path);
  if (!locale) return path;

  // Remove locale from path
  return path.replace(new RegExp(`^\\/${locale}`), "") || "/";
}

/**
 * Localize a path with a specific locale
 */
export function localizePath(path: string, locale: string): string {
  // If it's an absolute URL or anchor, return as is
  if (path.startsWith("http") || path.startsWith("#")) {
    return path;
  }

  // Check if the path already has a locale prefix
  if (path.match(/^\/(en|ru|uz|ar)(\/|$)/)) {
    // Replace the existing locale with the new one
    return path.replace(/^\/(en|ru|uz|ar)/, `/${locale}`);
  }

  // Ensure path starts with a slash
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  // Add locale prefix
  return `/${locale}${normalizedPath}`;
}

/**
 * Navigation guard for automatically adding locale prefix to routes
 */
export function setupLocaleNavigationGuard(router: Router): void {
  router.beforeEach((to, from, next) => {
    // If the route is already localized, proceed
    if (to.path.match(/^\/(en|ru|uz|ar)(\/|$)/)) {
      return next();
    }

    // Get the current locale from the path or localStorage
    const locale =
      getLocaleFromPath(from.path) || localStorage.getItem("locale") || "en";

    // Redirect to the localized version of the requested route
    const localizedPath = localizePath(to.path, locale);

    if (to.path !== localizedPath) {
      return next(localizedPath);
    }

    return next();
  });
}

/**
 * Plugin to install localized routing components and utilities
 */
export default {
  install(app: App, options: { router?: Router } = {}) {
    // Register the localized link component
    app.component("LocalizedLink", CLocalizedLink);

    // Set up global properties for localized routing
    app.config.globalProperties.$localizePath = localizePath;
    app.config.globalProperties.$getLocaleFromPath = getLocaleFromPath;
    app.config.globalProperties.$getPathWithoutLocale = getPathWithoutLocale;

    // Set up navigation guard if router is provided
    if (options.router) {
      setupLocaleNavigationGuard(options.router);
    }
  },
};
